/* =========================================================
   2NUT — Inline pack size selector on product cards
   Custom element: <pack-selector>
   ========================================================= */

class PackSelector extends HTMLElement {
  connectedCallback() {
    if (this._initialised) return;
    this._initialised = true;

    this.variants = this._parseVariants();
    this.card = this.closest('product-card');
    this.priceEl = this.querySelector('[data-pack-price]');

    if (this.card) {
      this.card.classList.add('has-pack-selector');
      this.card.classList.add('pack-align-' + (this.dataset.align || 'center'));
    }

    this.addEventListener('click', this._onClick.bind(this));
  }

  _parseVariants() {
    try {
      return JSON.parse(this.dataset.variants || '[]');
    } catch (e) {
      console.warn('[pack-selector] invalid variant data', e);
      return [];
    }
  }

  _onClick(event) {
    const btn = event.target.closest('.pack-selector__btn');
    if (!btn || !this.contains(btn)) return;

    // Card is usually wrapped in a link — stop it from navigating
    event.preventDefault();
    event.stopPropagation();

    this.select(btn);
  }

  select(btn) {
    const id = Number(btn.dataset.variantId);
    const variant = this.variants.find((v) => v.id === id);
    if (!variant) return;

    this.querySelectorAll('.pack-selector__btn').forEach((b) => {
      b.classList.remove('is-active');
      b.setAttribute('aria-pressed', 'false');
    });
    btn.classList.add('is-active');
    btn.setAttribute('aria-pressed', 'true');

    this.updatePrice(variant);
    this.updateForms(variant);
    this.updateLinks(variant);

    if (this.dataset.changeImage === 'true' && variant.image) {
      this.updateImage(variant);
    }

    this.dispatchEvent(
      new CustomEvent('pack-selector:change', { bubbles: true, detail: { variant } })
    );
  }

  updatePrice(variant) {
    if (!this.priceEl) return;

    let html = '<span class="pack-selector__price-current">' + variant.price + '</span>';

    if (variant.compare) {
      html += '<s class="pack-selector__price-compare">' + variant.compare + '</s>';
      if (this.dataset.showSavings === 'true' && variant.save > 0) {
        html += '<span class="pack-selector__price-save">' + variant.save + '% OFF</span>';
      }
    }

    this.priceEl.innerHTML = html;
  }

  updateForms(variant) {
    const root = this.card || document;

    // Hidden variant input(s) inside every form on this card
    root.querySelectorAll('.product-card-item__form input[name="id"]').forEach((input) => {
      input.value = variant.id;
    });

    // Named form fallback (in case the form sits outside <product-card>)
    if (this.dataset.formId) {
      const namedForm = document.getElementById(this.dataset.formId);
      if (namedForm) {
        const input = namedForm.querySelector('input[name="id"]');
        if (input) input.value = variant.id;
      }
    }

    if (this.card) {
      this.card.setAttribute('product-id', variant.id);
      if (variant.url) {
        this.card.setAttribute('product-url', variant.url.split('?')[0]);
      }
    }

    // Add to cart button state
    const addText = this.dataset.textAdd || 'Add to cart';
    const soldText = this.dataset.textSoldout || 'Sold out';

    root
      .querySelectorAll('.product-bottom-info .product-card-item__form button[type="submit"]')
      .forEach((button) => {
        button.disabled = !variant.available;
        button.classList.toggle('disable', !variant.available);
        const label = button.querySelector('.btn--text');
        if (label) {
          label.textContent = variant.available ? addText : soldText;
        } else {
          button.textContent = variant.available ? addText : soldText;
        }
        button.setAttribute('aria-label', variant.available ? addText : soldText);
      });
  }

  updateLinks(variant) {
    if (!this.card || !variant.url) return;
    this.card.querySelectorAll('a[href*="/products/"]').forEach((a) => {
      a.setAttribute('href', variant.url);
    });
  }

  updateImage(variant) {
    if (!this.card) return;
    const img = this.card.querySelector('.primary_image, .product_featured_image');
    if (!img) return;
    img.removeAttribute('srcset');
    img.setAttribute('src', variant.image);
  }
}

if (!customElements.get('pack-selector')) {
  customElements.define('pack-selector', PackSelector);
}